import { carePlanIdSchema, patientIdSchema } from "@repo/api/schemas";
import { useCarePlan, useCase, useConditions, useOrderSets, useSymptoms } from "@repo/data-store";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { CarePlanDetail } from "@/features/cases";

export const Route = createFileRoute("/patients/$patientId/_layout/care-plans/$carePlanId")({
  component: RouteComponent,
});

/**
 * Care-plan detail. Clinical curation lives on care-plan-scoped OrderSets.
 */
function RouteComponent() {
  const { patientId, carePlanId } = Route.useParams();
  const parsedPatientId = patientIdSchema.parse(patientId);
  const parsedCarePlanId = carePlanIdSchema.parse(carePlanId);
  const carePlan = useCarePlan(parsedCarePlanId);
  const { conditions } = useConditions({ patientId: parsedPatientId });
  const { symptoms } = useSymptoms({ patientId: parsedPatientId });
  const caseModel = useCase(carePlan?.episodeOfCareId);
  const orderSets = useOrderSets({ carePlanId: parsedCarePlanId, status: ["draft", "active", "completed"] });

  /** Candidate conditions for this plan's `addresses[]` = the case's diagnoses. */
  const caseDiagnosisIds = useMemo(() => new Set(caseModel?.diagnosis ?? []), [caseModel]);
  const candidates = useMemo(
    () => conditions.filter((c) => caseDiagnosisIds.has(c.conditionId)),
    [conditions, caseDiagnosisIds],
  );

  if (carePlan) {
    return (
      <CarePlanDetail
        carePlan={carePlan}
        patientId={patientId}
        conditions={conditions}
        candidateConditions={candidates}
        symptoms={symptoms}
        orderSets={orderSets}
      />
    );
  }
  return <div className="text-muted-foreground p-4">Care plan not found.</div>;
}
